/**
 * F-36: an annual billing option is one purchase a year, split on the card
 * (up to `maxInstallments`, at most 12). PIX and boleto pay the year up front.
 * The total is the same at 1× and 12× today; installment interest, and who
 * pays it, becomes an option of its own (F-43).
 */

interface BillingShape {
  billingInterval?: string | null
  maxInstallments?: number | string | null
  price?: number | string
}

const MAX_INSTALLMENTS = 12

/** Months covered by one charge, for the intervals the pricing page compares. */
const MONTHS_PER_CHARGE: Record<string, number> = {
  MONTHLY: 1,
  YEARLY: 12
}

export const isYearly = (product: BillingShape | null | undefined): boolean => product?.billingInterval === 'YEARLY'

/** Installments a yearly option offers on the card; 1 for anything else. */
export const yearlyInstallments = (product: BillingShape | null | undefined): number =>
  isYearly(product) ? Math.max(1, Math.min(MAX_INSTALLMENTS, Number(product?.maxInstallments) || 1)) : 1

/**
 * The price spread per month, so a monthly and an annual option compare on
 * the same footing (R$ 1.188/year reads as R$ 99/month). Intervals shorter
 * than a month keep their own price.
 */
export const pricePerMonth = (product: BillingShape): number => {
  const price = typeof product.price === 'string' ? parseFloat(product.price) : Number(product.price) || 0
  const months = MONTHS_PER_CHARGE[product.billingInterval || '']
  return months ? price / months : price
}

/** An option as the toggle reads it: its interval and the server's saving (API-189). */
interface SavingsOption {
  billingInterval?: string | null
  savingsPercentage?: number | null
}

/** Percent saved per month against the no-contract monthly option, from the API (API-189); null shows no tag. */
export const savingsOf = (option: SavingsOption | null | undefined): number | null =>
  typeof option?.savingsPercentage === 'number' && option.savingsPercentage > 0 ? option.savingsPercentage : null

/**
 * The "Anual" tag of a billing toggle over several plans, from the API's
 * `savingsPercentage` (the price paid per month against the no-contract
 * monthly one; API-189): "−17%" when every annual option saves the same,
 * "até −20%" (the largest) when they differ, nothing when none saves.
 */
export const yearlyToggleSavings = (plans: SavingsOption[][]): { percent: number; upTo: boolean } | null => {
  const yearly = plans.flat().filter((option) => option.billingInterval === 'YEARLY')
  const values = yearly.map(savingsOf).filter((value): value is number => value !== null)
  if (values.length === 0) return null
  return { percent: Math.max(...values), upTo: new Set(values).size > 1 || values.length < yearly.length }
}
